import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Cpu, Users, KeyRound, Home, RotateCcw, Share2, Copy, Trophy, Coins, Ghost, Zap } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "../../lib/api";
import { useTheme } from "../../context/ThemeContext";
import { ThemedShell, ThemedCard } from "../../components/child/ThemedShell";
import { Button } from "../../components/ui/button";
import { sfx } from "../../lib/sound";
import { CountUp } from "../../components/common/CountUp";

const SAMPLE_DT = 0.05;
const HELMET = { host: "#2f7ff0", guest: "#e0405b" }; // blauw vs rood

function gapForPipe(seed, k, H, gap) {
  let t = (seed + k * 101 + 0x6D2B79F5) >>> 0;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  const r = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  const margin = gap / 2 + H * 0.10;
  return margin + r * (H - 2 * margin);
}

// Opening wordt geleidelijk smaller; ná 50 buizen extra moeilijk.
function gapForK(k, H) {
  const base = H * 0.36;
  const early = Math.min(k, 30) * H * 0.0022;
  const late = Math.max(0, k - 50) * H * 0.0024;
  return Math.max(H * 0.185, base - early - late);
}

export default function Vogelvlucht() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [params] = useSearchParams();
  const joinParam = params.get("code");

  const [phase, setPhase] = useState(joinParam ? "loading" : "menu"); // menu|choose|loading|ready|countdown|playing|over|share|lobby
  const [mode, setMode] = useState("solo"); // solo | versus-host | versus-guest | live-host | live-guest
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(() => Number(localStorage.getItem("flap_best") || 0));
  const [result, setResult] = useState(null);
  const [shareCode, setShareCode] = useState(null);
  const [hostName, setHostName] = useState(null);
  const [hostScore, setHostScore] = useState(null);
  const [joinInput, setJoinInput] = useState("");
  const [busy, setBusy] = useState(false);

  // live
  const [oppName, setOppName] = useState(null);
  const [oppReady, setOppReady] = useState(false);
  const [meReady, setMeReady] = useState(false);
  const [countNum, setCountNum] = useState(0);
  const [oppScore, setOppScore] = useState(0);

  const canvasRef = useRef(null);
  const dimRef = useRef({ W: 400, H: 600, dpr: 1 });
  const seedRef = useRef(1);
  const ghostRef = useRef(null);
  const ghostEndRef = useRef(0);
  const gameIdRef = useRef(null);
  const gRef = useRef(null);
  const rafRef = useRef(0);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const modeRef = useRef(mode);
  modeRef.current = mode;

  // live refs
  const liveIdRef = useRef(null);
  const roleRef = useRef("host");
  const startAtRef = useRef(0);
  const serverOffRef = useRef(0);
  const oppRef = useRef({ samples: [], score: 0, done: false, crash: 0, name: "" });
  const liveResultRef = useRef(null);
  const localDoneRef = useRef(false);

  const C = theme.colors;
  const isLive = mode === "live-host" || mode === "live-guest";
  const isLiveRef = useRef(false);
  isLiveRef.current = isLive;

  useEffect(() => { if (joinParam) doJoin(joinParam); }, []); // eslint-disable-line

  const sizeCanvas = () => {
    const cv = canvasRef.current;
    if (!cv) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const W = cv.clientWidth, H = cv.clientHeight;
    cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr);
    dimRef.current = { W, H, dpr };
  };

  const resetWorld = () => {
    const { H } = dimRef.current;
    gRef.current = { t: 0, birdY: H * 0.42, v: 0, D: 0, score: 0, counted: new Set(), alive: true, samples: [], sampleAcc: 0, dead: false, deadAt: 0, boom: null, live: isLiveRef.current };
    setScore(0);
    localDoneRef.current = false;
  };

  // ---- physics + render ----
  const consts = () => {
    const { W, H } = dimRef.current;
    return { spacing: W * 1.0, pw: W * 0.16, firstX: W * 1.45, birdX: W * 0.26, groundH: H * 0.08, r: H * 0.028 };
  };
  const speedAt = (t, W) => Math.min(W * 0.9, W * 0.36 + W * 0.016 * t);

  const step = (dt) => {
    const g = gRef.current; const { W, H } = dimRef.current;
    if (!g) return;
    if (!g.alive && !g.live) return;
    g.t += dt;
    const sp = speedAt(g.t, W);
    g.D += sp * dt;

    const { spacing, pw, firstX, birdX, groundH, r } = consts();

    if (g.alive) {
      g.v += H * 1.5 * dt;
      g.birdY += g.v * dt;
      g.sampleAcc += dt;
      while (g.sampleAcc >= SAMPLE_DT) {
        g.sampleAcc -= SAMPLE_DT;
        g.samples.push(Math.max(0, Math.min(1000, Math.round((g.birdY / H) * 1000))));
      }
      if (g.birdY - r < 0) { g.birdY = r; g.v = 0; }
      if (g.birdY + r > H - groundH) { g.birdY = H - groundH - r; die(); return; }

      const k0 = Math.max(0, Math.floor((g.D - firstX) / spacing) - 1);
      for (let k = k0; k <= k0 + 4; k++) {
        const screenX = firstX + k * spacing - g.D;
        if (screenX + pw < -pw) continue;
        if (screenX > W) break;
        const gap = gapForK(k, H);
        const c = gapForPipe(seedRef.current, k, H, gap);
        const gapTop = c - gap / 2, gapBot = c + gap / 2;
        if (!g.counted.has(k) && screenX + pw < birdX) { g.counted.add(k); g.score++; setScore(g.score); sfx.coin(); }
        if (birdX + r > screenX && birdX - r < screenX + pw) {
          if (g.birdY - r < gapTop || g.birdY + r > gapBot) { die(); return; }
        }
      }
    } else {
      // dood in live: val naar de grond, wereld blijft scrollen zodat de ander doorvliegt
      g.v += H * 1.5 * dt;
      g.birdY = Math.min(H - groundH - r, g.birdY + g.v * dt);
      if (g.boom) g.boom.t += dt;
    }
  };

  const die = () => {
    const g = gRef.current;
    if (g.dead) return;
    g.alive = false; g.dead = true; g.deadAt = g.t; g.boom = { t: 0, y: g.birdY };
    sfx.bang();
    if (g.live) {
      localDoneRef.current = true;
      postLiveState(true);
    } else {
      finishRun(g.score, g.samples, g.t);
    }
  };

  const roundedRect = (ctx, x, y, w, h, rad) => {
    ctx.beginPath();
    ctx.moveTo(x + rad, y);
    ctx.arcTo(x + w, y, x + w, y + h, rad);
    ctx.arcTo(x + w, y + h, x, y + h, rad);
    ctx.arcTo(x, y + h, x, y, rad);
    ctx.arcTo(x, y, x + w, y, rad);
    ctx.closePath();
  };

  // Vogel met gekleurde helm (blauw=host, rood=gast).
  const drawHelmetBird = (ctx, x, y, ang, helmet, r, alpha) => {
    ctx.save();
    ctx.globalAlpha = alpha == null ? 1 : alpha;
    ctx.translate(x, y); ctx.rotate(ang);
    const R = r * 1.55;
    // lijf
    ctx.fillStyle = "#ffd84a"; ctx.beginPath(); ctx.arc(0, 0, R, 0, 7); ctx.fill();
    // vleugel
    ctx.fillStyle = "#f0a500"; ctx.beginPath(); ctx.ellipse(-R * 0.2, R * 0.25, R * 0.6, R * 0.4, 0, 0, 7); ctx.fill();
    // oog
    ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.arc(R * 0.42, -R * 0.28, R * 0.32, 0, 7); ctx.fill();
    ctx.fillStyle = "#111"; ctx.beginPath(); ctx.arc(R * 0.52, -R * 0.28, R * 0.14, 0, 7); ctx.fill();
    // snavel
    ctx.fillStyle = "#ff8c1a"; ctx.beginPath(); ctx.moveTo(R * 0.9, -R * 0.05); ctx.lineTo(R * 1.5, R * 0.08); ctx.lineTo(R * 0.9, R * 0.3); ctx.closePath(); ctx.fill();
    // helm (koepel + rand)
    ctx.fillStyle = helmet;
    ctx.beginPath(); ctx.arc(0, -R * 0.15, R * 1.02, Math.PI * 1.02, Math.PI * 1.98); ctx.fill();
    ctx.fillRect(-R * 1.0, -R * 0.35, R * 2.0, R * 0.34);
    ctx.fillStyle = "rgba(255,255,255,0.35)"; ctx.fillRect(-R * 0.5, -R * 0.95, R * 0.55, R * 0.5);
    ctx.restore();
  };

  const draw = () => {
    const cv = canvasRef.current; if (!cv) return;
    const ctx = cv.getContext("2d");
    const { W, H, dpr } = dimRef.current;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const g = gRef.current;
    const night = Math.floor((g?.score || 0) / 6) % 2 === 1;

    const sky = ctx.createLinearGradient(0, 0, 0, H);
    if (night) { sky.addColorStop(0, "#0c1530"); sky.addColorStop(1, "#26335e"); }
    else { sky.addColorStop(0, "#8fd9ff"); sky.addColorStop(1, "#d7f1ff"); }
    ctx.fillStyle = sky; ctx.fillRect(0, 0, W, H);

    if (night) {
      ctx.fillStyle = "#fdf6c8"; ctx.beginPath(); ctx.arc(W * 0.8, H * 0.18, H * 0.05, 0, 7); ctx.fill();
      ctx.fillStyle = "rgba(255,255,255,0.85)";
      for (let i = 0; i < 26; i++) { const sx = ((i * 167) % W), sy = ((i * 91) % (H * 0.6)); ctx.fillRect(sx, sy, 2, 2); }
    } else {
      ctx.fillStyle = "#fff3b0"; ctx.beginPath(); ctx.arc(W * 0.8, H * 0.16, H * 0.06, 0, 7); ctx.fill();
      ctx.fillStyle = "rgba(255,255,255,0.7)";
      const off = ((g?.D || 0) * 0.2) % (W + 160);
      for (const cx of [W * 0.2 - off, W * 0.6 - off, W * 1.0 - off, W * 1.4 - off]) {
        ctx.beginPath(); ctx.arc(cx, H * 0.22, 24, 0, 7); ctx.arc(cx + 26, H * 0.22, 30, 0, 7); ctx.arc(cx + 54, H * 0.22, 22, 0, 7); ctx.fill();
      }
    }

    const { spacing, pw, firstX, birdX, groundH, r } = consts();

    const pipeCol = night ? "#3ad1b5" : "#2bb673";
    const pipeDark = night ? "#2aa491" : "#1f8d57";
    if (g) {
      const k0 = Math.max(0, Math.floor((g.D - firstX) / spacing) - 1);
      for (let k = k0; k <= k0 + 4; k++) {
        const x = firstX + k * spacing - g.D;
        if (x + pw < 0 || x > W) continue;
        const gap = gapForK(k, H);
        const c = gapForPipe(seedRef.current, k, H, gap);
        const gapTop = c - gap / 2, gapBot = c + gap / 2;
        ctx.fillStyle = pipeCol;
        roundedRect(ctx, x, 0, pw, gapTop, 8); ctx.fill();
        roundedRect(ctx, x, gapBot, pw, H - groundH - gapBot, 8); ctx.fill();
        ctx.fillStyle = pipeDark;
        roundedRect(ctx, x - 4, gapTop - 16, pw + 8, 16, 6); ctx.fill();
        roundedRect(ctx, x - 4, gapBot, pw + 8, 16, 6); ctx.fill();
      }
    }

    ctx.fillStyle = night ? "#1b2440" : "#6bbf63";
    ctx.fillRect(0, H - groundH, W, groundH);
    ctx.fillStyle = night ? "#141b30" : "#5aa854";
    const goff = ((g?.D || 0) % 40);
    for (let x = -40; x < W; x += 40) ctx.fillRect(x - goff, H - groundH, 20, 6);

    // ---- LIVE: tegenstander-vogel uit samples ----
    if (isLiveRef.current && g) {
      const arr = oppRef.current.samples || [];
      const idx = Math.floor(g.t / SAMPLE_DT);
      if (arr.length) {
        const gy = (arr[Math.min(idx, arr.length - 1)] / 1000) * H;
        const oppHelmet = roleRef.current === "host" ? HELMET.guest : HELMET.host;
        drawHelmetBird(ctx, birdX, gy, 0.15, oppHelmet, r, oppRef.current.done && idx > arr.length ? 0.5 : 0.95);
      }
    }

    // ---- shadow-ghost (versus-guest) ----
    if (!isLiveRef.current && ghostRef.current && g) {
      const idx = Math.floor(g.t / SAMPLE_DT);
      const arr = ghostRef.current;
      if (idx < arr.length) {
        const gy = (arr[idx] / 1000) * H;
        ctx.save(); ctx.globalAlpha = 0.42;
        ctx.beginPath(); ctx.arc(birdX, gy, r * 1.5, 0, 7); ctx.fillStyle = night ? "#ffd166" : "#ff5d8f"; ctx.fill();
        ctx.globalAlpha = 0.85; ctx.font = `${r * 2.4}px serif`; ctx.textAlign = "center"; ctx.textBaseline = "middle";
        ctx.fillText(theme.mascot, birdX, gy);
        ctx.restore();
      }
    }

    // ---- speler ----
    if (g) {
      const ang = Math.max(-0.5, Math.min(1.1, g.v / (H * 1.2)));
      if (isLiveRef.current) {
        drawHelmetBird(ctx, birdX, g.birdY, ang, HELMET[roleRef.current], r);
      } else {
        ctx.save();
        ctx.translate(birdX, g.birdY); ctx.rotate(ang);
        ctx.beginPath(); ctx.arc(0, 0, r * 1.55, 0, 7);
        ctx.fillStyle = C.accent; ctx.fill();
        ctx.font = `${r * 2.6}px serif`; ctx.textAlign = "center"; ctx.textBaseline = "middle";
        ctx.fillText(theme.mascot, 0, 0);
        ctx.restore();
      }
      // knal-explosie
      if (g.boom && g.boom.t < 0.5) {
        const bt = g.boom.t / 0.5;
        ctx.save(); ctx.globalAlpha = 1 - bt;
        ctx.strokeStyle = "#ffcf3a"; ctx.lineWidth = 4;
        ctx.beginPath(); ctx.arc(birdX, g.boom.y, r * 2 + bt * r * 5, 0, 7); ctx.stroke();
        ctx.fillStyle = "#ff6a3a"; ctx.font = `900 ${r * 2.4}px system-ui`; ctx.textAlign = "center"; ctx.textBaseline = "middle";
        ctx.fillText("BOEM!", birdX, g.boom.y - r * 3);
        ctx.restore();
      }
    }
  };

  // loop
  useEffect(() => {
    let last = 0;
    const loop = (ts) => {
      if (!last) last = ts;
      let dt = (ts - last) / 1000; last = ts;
      if (dt > 0.033) dt = 0.033;
      if (phaseRef.current === "playing") step(dt);
      draw();
      if (["playing", "ready", "over", "countdown"].includes(phaseRef.current)) rafRef.current = requestAnimationFrame(loop);
    };
    if (["ready", "playing", "over", "countdown"].includes(phase)) {
      sizeCanvas();
      if ((phase === "ready" || phase === "countdown") && !gRef.current) resetWorld();
      rafRef.current = requestAnimationFrame(loop);
    }
    return () => cancelAnimationFrame(rafRef.current);
  }, [phase]); // eslint-disable-line

  const flap = () => {
    if (isLiveRef.current) { if (phaseRef.current !== "playing") return; }
    else if (phaseRef.current === "ready") { setPhase("playing"); }
    const g = gRef.current;
    if (g && g.alive && phaseRef.current === "playing") { g.v = -dimRef.current.H * 0.52; sfx.click(); }
  };

  useEffect(() => {
    const onKey = (e) => { if (e.code === "Space") { e.preventDefault(); flap(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []); // eslint-disable-line

  // ---- SOLO / SHADOW API ----
  const startSolo = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/child/flap/new", { mode: "solo" });
      seedRef.current = data.seed; gameIdRef.current = data.game_id; ghostRef.current = null;
      setMode("solo"); setResult(null); gRef.current = null; setPhase("ready");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };

  const startVersusHost = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/child/flap/new", { mode: "versus" });
      seedRef.current = data.seed; gameIdRef.current = data.game_id; ghostRef.current = null;
      setShareCode(data.share_code); setMode("versus-host"); setResult(null); gRef.current = null; setPhase("ready");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };

  const doJoin = async (code) => {
    setBusy(true); setPhase("loading");
    const clean = code.trim().toUpperCase();
    try {
      // eerst live proberen
      const live = await api.get(`/child/flap-live/join/${clean}`);
      if (!live.data.not_found) {
        applyLivePub(live.data, false);
        return;
      }
      // anders: shadow-race
      const { data } = await api.get(`/child/flap/join/${clean}`);
      if (data.not_found) { toast.error("Geen uitdaging gevonden met die code"); setPhase("menu"); setBusy(false); return; }
      if (data.waiting) { toast.message(`${data.host_name || "Je vriend"} speelt nog. Probeer zo opnieuw!`); setPhase("menu"); setBusy(false); return; }
      if (data.role === "host") { toast.message("Dit is jouw eigen uitdaging."); setPhase("menu"); setBusy(false); return; }
      seedRef.current = data.seed; gameIdRef.current = data.game_id;
      ghostRef.current = data.ghost; ghostEndRef.current = data.ghost_end_t;
      setHostName(data.host_name); setHostScore(data.host_score);
      setMode("versus-guest"); setResult(null); gRef.current = null; setPhase("ready");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); setPhase("menu"); }
    setBusy(false);
  };

  const finishRun = async (sc, samples, endT) => {
    setPhase("over");
    if (sc > best) { setBest(sc); localStorage.setItem("flap_best", String(sc)); }
    try {
      const { data } = await api.post(`/child/flap/${gameIdRef.current}/run`, { samples, score: sc, end_t: endT });
      setResult(data); sfx.win();
      if (mode === "versus-host") setShareCode(data.share_code);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const playAgainSolo = () => { gRef.current = null; setResult(null); startSolo(); };

  // ---- LIVE API ----
  const applyLivePub = (data, asHost) => {
    liveIdRef.current = data.game_id;
    seedRef.current = data.seed;
    roleRef.current = data.role;
    setMode(data.role === "host" ? "live-host" : "live-guest");
    isLiveRef.current = true;
    setShareCode(data.share_code);
    setOppName(data.opp_name);
    setMeReady(data.me_ready); setOppReady(data.opp_ready);
    setResult(null); gRef.current = null; liveResultRef.current = null; localDoneRef.current = false;
    oppRef.current = { samples: [], score: 0, done: false, crash: 0, name: data.opp_name };
    if (data.start_at) { startAtRef.current = data.start_at; serverOffRef.current = data.server_now - Date.now() / 1000; }
    setPhase("lobby");
    setBusy(false);
  };

  const startLiveHost = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/child/flap-live/new");
      applyLivePub(data, true);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); setPhase("menu"); setBusy(false); }
  };

  const liveReady = async () => {
    try {
      const { data } = await api.post(`/child/flap-live/${liveIdRef.current}/ready`);
      setMeReady(true); setOppReady(data.opp_ready); setOppName(data.opp_name);
      if (data.start_at) { startAtRef.current = data.start_at; serverOffRef.current = data.server_now - Date.now() / 1000; setPhase("countdown"); }
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  // Lobby-poll: wacht op vriend + op start.
  useEffect(() => {
    if (phase !== "lobby") return;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get(`/child/flap-live/${liveIdRef.current}`);
        if (!alive) return;
        setOppName(data.opp_name); setOppReady(data.opp_ready); setMeReady(data.me_ready);
        if (data.start_at) { startAtRef.current = data.start_at; serverOffRef.current = data.server_now - Date.now() / 1000; setPhase("countdown"); }
      } catch { /* ignore */ }
    };
    const id = setInterval(tick, 1400); tick();
    return () => { alive = false; clearInterval(id); };
  }, [phase]);

  // Countdown -> playing (gesynchroniseerd via start_at).
  useEffect(() => {
    if (phase !== "countdown") return;
    if (!gRef.current) resetWorld();
    const id = setInterval(() => {
      const remain = startAtRef.current - (Date.now() / 1000 + serverOffRef.current);
      if (remain <= 0) { setCountNum(0); clearInterval(id); sfx.go(); setPhase("playing"); }
      else setCountNum(Math.ceil(remain));
    }, 100);
    return () => clearInterval(id);
  }, [phase]);

  const postLiveState = async (done) => {
    const g = gRef.current;
    try {
      const { data } = await api.post(`/child/flap-live/${liveIdRef.current}/state`, {
        samples: g ? g.samples : [], score: g ? g.score : 0, done: !!done, crash_t: g ? g.deadAt : 0,
      });
      oppRef.current = { samples: data.opp_samples || [], score: data.opp_score || 0, done: !!data.opp_done, crash: data.opp_crash_t || 0, name: data.opp_name };
      setOppScore(data.opp_score || 0); setOppName(data.opp_name);
      if (data.result) liveResultRef.current = data.result;
      if (data.status === "done" && data.result) finalizeLive(data.result);
    } catch { /* ignore transient */ }
  };

  const finalizeLive = (res) => {
    if (phaseRef.current === "over") return;
    setResult(res); setPhase("over");
    if (res.won) sfx.win(); else sfx.wrong();
    if ((gRef.current?.score || 0) > best) { setBest(gRef.current.score); localStorage.setItem("flap_best", String(gRef.current.score)); }
  };

  // Live state-poll tijdens countdown + playing.
  useEffect(() => {
    if (!isLive) return;
    if (phase !== "countdown" && phase !== "playing") return;
    let alive = true;
    const id = setInterval(() => { if (alive) postLiveState(localDoneRef.current); }, 300);
    return () => { alive = false; clearInterval(id); };
  }, [phase, isLive]); // eslint-disable-line

  // Fallback: als beide dood zijn maar backend traag finaliseert, blijf pollen.
  const muted = { color: "var(--t-muted)" };

  // ---------- MENU ----------
  if (phase === "menu")
    return (
      <ThemedShell>
        <div className="max-w-md mx-auto px-5 pt-8 pb-16">
          <div className="flex items-center gap-2 mb-6">
            <Button variant="ghost" onClick={() => navigate("/leerling/speelhoek")} className="rounded-full px-3" style={{ color: "var(--t-text)" }} data-testid="flap-back"><ArrowLeft className="w-5 h-5" /></Button>
            <h1 className="font-heading text-2xl font-black themed-heading">Vogelvlucht {theme.mascot}</h1>
          </div>
          <p className="font-semibold mb-6" style={muted}>Tik om te fladderen en ontwijk de buizen! Hoe verder je komt, hoe smaller de opening — en ná 50 buizen wordt het extra pittig. 🌙</p>

          <button onClick={startSolo} disabled={busy} data-testid="flap-solo-btn"
            className="w-full mb-3 rounded-3xl p-5 flex items-center gap-4 border-2 active:scale-[0.99] transition text-left"
            style={{ background: "var(--t-surface)", borderColor: "var(--t-accent)" }}>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "var(--t-accent)", color: "var(--t-on-accent)" }}><Cpu className="w-7 h-7" /></div>
            <div className="flex-1"><h3 className="font-heading text-lg font-extrabold themed-heading">Tegen de computer</h3><p className="text-sm font-semibold" style={muted}>Speel solo en verbeter je topscore. Beste: <b style={{ color: "var(--t-text)" }}>{best}</b></p></div>
          </button>

          <button onClick={() => setPhase("choose")} disabled={busy} data-testid="flap-versus-btn"
            className="w-full mb-3 rounded-3xl p-5 flex items-center gap-4 border-2 active:scale-[0.99] transition text-left"
            style={{ background: "var(--t-surface)", borderColor: "var(--t-primary)" }}>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}><Users className="w-7 h-7" /></div>
            <div className="flex-1"><h3 className="font-heading text-lg font-extrabold themed-heading">Daag een vriend uit</h3><p className="text-sm font-semibold" style={muted}>Live samen tegelijk vliegen, of tegen elkaars schaduw!</p></div>
          </button>

          <ThemedCard className="p-5">
            <h3 className="font-heading font-extrabold mb-2 flex items-center gap-2 themed-heading"><KeyRound className="w-5 h-5" style={{ color: "var(--t-accent)" }} /> Doe mee met een code</h3>
            <div className="flex gap-2">
              <input value={joinInput} onChange={(e) => setJoinInput(e.target.value.toUpperCase())} placeholder="bijv. K7M2P" maxLength={6} data-testid="flap-join-input"
                className="flex-1 h-12 rounded-xl px-4 font-heading font-black tracking-widest text-lg border-2 outline-none"
                style={{ background: "var(--t-surface2)", borderColor: "var(--t-border)", color: "var(--t-text)" }} />
              <Button onClick={() => joinInput.trim() && doJoin(joinInput)} disabled={busy || !joinInput.trim()} data-testid="flap-join-btn"
                className="h-12 rounded-xl font-bold border-0 px-5" style={{ background: "var(--t-accent)", color: "var(--t-on-accent)" }}>Mee doen</Button>
            </div>
          </ThemedCard>
        </div>
      </ThemedShell>
    );

  // ---------- CHOOSE (schaduw vs live) ----------
  if (phase === "choose")
    return (
      <ThemedShell>
        <div className="max-w-md mx-auto px-5 pt-8 pb-16">
          <div className="flex items-center gap-2 mb-6">
            <Button variant="ghost" onClick={() => setPhase("menu")} className="rounded-full px-3" style={{ color: "var(--t-text)" }} data-testid="flap-choose-back"><ArrowLeft className="w-5 h-5" /></Button>
            <h1 className="font-heading text-2xl font-black themed-heading">Daag een vriend uit</h1>
          </div>

          <button onClick={startLiveHost} disabled={busy} data-testid="flap-live-btn"
            className="w-full mb-3 rounded-3xl p-5 flex items-center gap-4 border-2 active:scale-[0.99] transition text-left"
            style={{ background: "var(--t-surface)", borderColor: "var(--t-primary)" }}>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}><Zap className="w-7 h-7" /></div>
            <div className="flex-1"><h3 className="font-heading text-lg font-extrabold themed-heading">Live samen 🔵🔴</h3><p className="text-sm font-semibold" style={muted}>Vlieg <b>tegelijk</b> op je eigen apparaat door dezelfde buizen. Jij hebt de <b style={{ color: HELMET.host }}>blauwe helm</b>, je vriend de <b style={{ color: HELMET.guest }}>rode</b>.</p></div>
          </button>

          <button onClick={startVersusHost} disabled={busy} data-testid="flap-shadow-btn"
            className="w-full mb-3 rounded-3xl p-5 flex items-center gap-4 border-2 active:scale-[0.99] transition text-left"
            style={{ background: "var(--t-surface)", borderColor: "var(--t-accent)" }}>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "var(--t-accent)", color: "var(--t-on-accent)" }}><Ghost className="w-7 h-7" /></div>
            <div className="flex-1"><h3 className="font-heading text-lg font-extrabold themed-heading">Schaduw-race 👻</h3><p className="text-sm font-semibold" style={muted}>Jij vliegt eerst, deelt je code — je vriend vliegt later tegen jouw schaduw.</p></div>
          </button>
        </div>
      </ThemedShell>
    );

  if (phase === "loading")
    return <ThemedShell><div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 rounded-full border-4 animate-spin" style={{ borderColor: "var(--t-surface2)", borderTopColor: "var(--t-accent)" }} /></div></ThemedShell>;

  // ---------- LOBBY (live: wachten + klaar) ----------
  if (phase === "lobby")
    return (
      <ThemedShell>
        <div className="max-w-md mx-auto px-5 pt-10 pb-16 text-center">
          <div className="flex items-center gap-2 mb-6">
            <Button variant="ghost" onClick={() => { liveIdRef.current = null; isLiveRef.current = false; setPhase("menu"); }} className="rounded-full px-3" style={{ color: "var(--t-text)" }} data-testid="flap-lobby-back"><ArrowLeft className="w-5 h-5" /></Button>
            <h1 className="font-heading text-2xl font-black themed-heading">Live uitdaging ⚡</h1>
          </div>

          {roleRef.current === "host" && (
            <ThemedCard className="p-6 mb-5">
              <p className="text-sm font-bold mb-2" style={muted}>Deel deze code met je vriend</p>
              <div className="font-heading text-5xl font-black tracking-[0.3em] themed-heading" data-testid="flap-lobby-code">{shareCode}</div>
              <div className="flex gap-2 mt-5">
                <Button onClick={() => { navigator.clipboard?.writeText(shareCode); toast.success("Code gekopieerd!"); }} data-testid="flap-lobby-copy" className="flex-1 h-11 rounded-xl font-bold border-2 bg-transparent" style={{ borderColor: "var(--t-border)", color: "var(--t-text)" }}><Copy className="w-4 h-4 mr-1.5" /> Code</Button>
                <Button onClick={() => { const url = `${window.location.origin}/leerling/vogelvlucht?code=${shareCode}`; navigator.clipboard?.writeText(url); toast.success("Link gekopieerd!"); }} data-testid="flap-lobby-link" className="flex-1 h-11 rounded-xl font-bold border-0" style={{ background: "var(--t-accent)", color: "var(--t-on-accent)" }}><Share2 className="w-4 h-4 mr-1.5" /> Link</Button>
              </div>
            </ThemedCard>
          )}

          <ThemedCard className="p-6 mb-5">
            <div className="flex items-center justify-around">
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-full" style={{ background: HELMET[roleRef.current] }} />
                <span className="font-bold text-sm">Jij {meReady ? "✅" : "…"}</span>
              </div>
              <span className="font-heading text-2xl font-black" style={muted}>VS</span>
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 rounded-full" style={{ background: roleRef.current === "host" ? HELMET.guest : HELMET.host, opacity: oppName ? 1 : 0.25 }} />
                <span className="font-bold text-sm">{oppName || "wachten…"} {oppName ? (oppReady ? "✅" : "…") : ""}</span>
              </div>
            </div>
          </ThemedCard>

          {!oppName ? (
            <p className="font-semibold" style={muted} data-testid="flap-lobby-wait">Wachten tot je vriend meedoet met code <b>{shareCode}</b>…</p>
          ) : !meReady ? (
            <Button onClick={liveReady} data-testid="flap-ready-btn" className="w-full h-12 rounded-xl font-bold border-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}>Ik ben klaar! 🚀</Button>
          ) : (
            <p className="font-semibold" style={muted} data-testid="flap-lobby-waitready">Wachten tot {oppName} ook klaar is…</p>
          )}
        </div>
      </ThemedShell>
    );

  // ---------- SHARE (versus host na vlucht) ----------
  if (phase === "share")
    return (
      <ThemedShell>
        <div className="max-w-md mx-auto px-5 pt-10 pb-16 text-center">
          <div className="text-5xl mb-3">🐤🏁</div>
          <h1 className="font-heading text-2xl font-black themed-heading">Jouw score: {score}</h1>
          <p className="font-semibold mt-2 mb-6" style={muted}>Deel deze code. Je vriend vliegt tegen jouw schaduw — wie komt het verst?</p>
          <ThemedCard className="p-7 mb-5">
            <p className="text-sm font-bold mb-2" style={muted}>Uitdagingscode</p>
            <div className="font-heading text-5xl font-black tracking-[0.3em] themed-heading" data-testid="flap-share-code">{shareCode}</div>
            <div className="flex gap-2 mt-5">
              <Button onClick={() => { navigator.clipboard?.writeText(shareCode); toast.success("Code gekopieerd!"); }} data-testid="flap-copy-code" className="flex-1 h-11 rounded-xl font-bold border-2 bg-transparent" style={{ borderColor: "var(--t-border)", color: "var(--t-text)" }}><Copy className="w-4 h-4 mr-1.5" /> Code</Button>
              <Button onClick={() => { const url = `${window.location.origin}/leerling/vogelvlucht?code=${shareCode}`; navigator.clipboard?.writeText(url); toast.success("Link gekopieerd!"); }} data-testid="flap-copy-link" className="flex-1 h-11 rounded-xl font-bold border-0" style={{ background: "var(--t-accent)", color: "var(--t-on-accent)" }}><Share2 className="w-4 h-4 mr-1.5" /> Link</Button>
            </div>
          </ThemedCard>
          <Button onClick={() => navigate("/leerling/speelhoek")} className="w-full h-11 rounded-xl font-bold border-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}><Home className="w-4 h-4 mr-1.5" /> Naar de speelhoek</Button>
        </div>
      </ThemedShell>
    );

  // ---------- PLAY (ready/countdown/playing/over) ----------
  return (
    <div className="fixed inset-0 overflow-hidden select-none" style={{ background: "#0a1020" }} data-testid="flap-stage">
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full touch-none"
        onPointerDown={(e) => { e.preventDefault(); if (phase !== "over") flap(); }} data-testid="flap-canvas" />

      {/* HUD score */}
      {(phase === "playing" || phase === "ready" || phase === "countdown") && (
        <div className="absolute top-0 inset-x-0 pt-6 text-center pointer-events-none">
          <div className="font-heading text-6xl font-black text-white drop-shadow-[0_3px_0_rgba(0,0,0,0.4)]" data-testid="flap-score">{score}</div>
          {mode === "versus-guest" && <p className="text-white/90 font-bold mt-1 drop-shadow">vs {hostName} ({hostScore})</p>}
          {isLive && (
            <div className="flex items-center justify-center gap-2 mt-1 drop-shadow" data-testid="flap-live-vs">
              <span className="inline-block w-3 h-3 rounded-full" style={{ background: HELMET[roleRef.current] }} />
              <span className="text-white/90 font-bold">vs {oppName || "…"}</span>
              <span className="inline-block w-3 h-3 rounded-full" style={{ background: roleRef.current === "host" ? HELMET.guest : HELMET.host }} />
              <span className="text-white/90 font-bold" data-testid="flap-opp-score">{oppScore}</span>
            </div>
          )}
        </div>
      )}

      {/* READY overlay (solo/shadow) */}
      {phase === "ready" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <motion.div animate={{ y: [0, -12, 0] }} transition={{ repeat: Infinity, duration: 1.4 }} className="text-5xl mb-4">{theme.mascot}</motion.div>
          <div className="px-6 py-3 rounded-2xl font-heading text-xl font-black text-white" style={{ background: "rgba(0,0,0,0.4)" }}>Tik om te starten 👆</div>
          {mode === "versus-guest" && <p className="text-white/90 font-bold mt-3">Versla {hostName}: {hostScore} punten</p>}
        </div>
      )}

      {/* COUNTDOWN overlay (live) */}
      {phase === "countdown" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none" data-testid="flap-countdown">
          <motion.div key={countNum} initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="font-heading text-8xl font-black text-white drop-shadow-[0_4px_0_rgba(0,0,0,0.5)]">
            {countNum > 0 ? countNum : "GO!"}
          </motion.div>
          <p className="text-white/90 font-bold mt-4">Blauw = jij? Tik straks om te fladderen!</p>
        </div>
      )}

      {/* Back button */}
      {phase !== "over" && phase !== "playing" && (
        <button onClick={() => { gRef.current = null; ghostRef.current = null; isLiveRef.current = false; liveIdRef.current = null; setPhase("menu"); }} className="absolute top-5 left-4 w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(0,0,0,0.35)" }} data-testid="flap-stage-back">
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
      )}

      {/* Wachten op tegenstander (live, na eigen crash) */}
      {phase === "playing" && isLive && localDoneRef.current && (
        <div className="absolute bottom-10 inset-x-0 text-center pointer-events-none">
          <div className="inline-block px-5 py-2 rounded-full font-bold text-white" style={{ background: "rgba(0,0,0,0.5)" }} data-testid="flap-wait-opp">💥 Af! Wachten op {oppName}…</div>
        </div>
      )}

      {/* GAME OVER */}
      {phase === "over" && (
        <div className="absolute inset-0 flex items-center justify-center px-5" style={{ background: "rgba(5,8,16,0.55)" }}>
          <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-full max-w-sm">
            <ThemedCard className="p-7 text-center" data-testid="flap-over">
              <div className="text-5xl mb-2">{result?.won ? "🏆" : result?.tie ? "🤝" : "🐤"}</div>
              <h1 className="font-heading text-3xl font-black themed-heading">{score} punten</h1>
              {(mode === "versus-guest" || isLive) && result && (
                <p className="font-bold mt-1" style={{ color: result.won ? "var(--t-success)" : "var(--t-primary)" }} data-testid="flap-versus-result">
                  {result.won ? `Gewonnen van ${oppName || hostName}! 🎉` : result.tie ? "Gelijkspel!" : `${result.winner || oppName || hostName} won${result.opponent_score != null ? ` (${result.opponent_score})` : ""}`}
                </p>
              )}
              <p className="font-semibold mt-1" style={muted}>Beste solo: {best}</p>
              {result && (result.xp_earned != null) && (
                <div className="flex justify-center gap-4 mt-4">
                  <span className="flex items-center gap-1.5 font-heading font-extrabold" style={{ color: "var(--t-accent)" }}><Trophy className="w-5 h-5" /> +<CountUp end={result.xp_earned} /> XP</span>
                  <span className="flex items-center gap-1.5 font-heading font-extrabold" style={{ color: "var(--t-primary)" }}><Coins className="w-5 h-5" /> +<CountUp end={result.coins_earned} /></span>
                </div>
              )}
              <div className="flex flex-col gap-2 mt-6">
                {mode === "versus-host" ? (
                  <Button onClick={() => setPhase("share")} data-testid="flap-share-btn" className="h-12 rounded-xl font-bold border-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}><Share2 className="w-4 h-4 mr-1.5" /> Deel uitdaging</Button>
                ) : isLive ? (
                  <Button onClick={() => { isLiveRef.current = false; liveIdRef.current = null; gRef.current = null; setResult(null); setPhase("choose"); }} data-testid="flap-again-btn" className="h-12 rounded-xl font-bold border-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}><RotateCcw className="w-4 h-4 mr-1.5" /> Nog een keer</Button>
                ) : (
                  <Button onClick={playAgainSolo} data-testid="flap-again-btn" className="h-12 rounded-xl font-bold border-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}><RotateCcw className="w-4 h-4 mr-1.5" /> Opnieuw</Button>
                )}
                <Button onClick={() => navigate("/leerling/speelhoek")} className="h-12 rounded-xl font-bold border-2 bg-transparent" style={{ borderColor: "var(--t-border)", color: "var(--t-text)" }}><Home className="w-4 h-4 mr-1.5" /> Speelhoek</Button>
              </div>
            </ThemedCard>
          </motion.div>
        </div>
      )}
    </div>
  );
}
